/**
 * filterLabel
 *
 * A readable description of an executed filter for chart subtitles (Bug 2754):
 * `ugcut_cp = 5`, `ugcut_cp = 5, 6` (OR), `ugcut_cp = 5, 6; mes = 8` (AND),
 * `0.5 ≤ cu ≤ 1` — instead of the filter's id (`m0f3 (combined)`), which tells the reader
 * nothing. The contract carries no human label, but the stored entry has all it
 * takes: the filter pool, each leaf's column position and the model's columns.
 *
 * Returns undefined whenever any part cannot be described faithfully, so the
 * caller keeps its `<id> (<type>)` fallback rather than printing half a filter.
 */
import type { AlgorithmModelColumn, CombinedFilter, FilterSpec } from '../types/compute';
import { expressionWithoutCutoff } from './errorCutoff';
import { isPeriodLeafId, periodOrFilterId } from './filterSpec';

type Columns = readonly AlgorithmModelColumn[] | undefined;

// How a rendered piece composes into a larger expression: an `atom` carries no
// operator of its own (a leaf, one column's values, a NOT of an atom); an `and`
// is a `; ` chain; an `expr` keeps AND/OR words (an OR across columns, a mix).
type Rendered = { text: string; kind: 'atom' | 'and' | 'expr' };

// Ids are word-char only (enforced by the FilterEditor); anything else that is
// not a parenthesis is a token this describer does not understand.
const TOKEN = /\(|\)|[A-Za-z0-9_]+|\S/g;
const ID = /^[A-Za-z0-9_]+$/;

/**
 * Describe the filter `id` of model `modelIndex`, whose column positions index
 * `columns`. Undefined for what the entry cannot name (a spatial solid, a
 * `true`/`false` pass, an unresolved column, an unbounded range) and for a
 * combined over the "categoría de interés" filters, which get their own segment.
 */
export function describeFilter(
  id: string,
  filters: readonly FilterSpec[],
  columns: Columns,
  modelIndex: number
): string | undefined {
  return describe(id, filters, columns, modelIndex, new Set())?.text;
}

function describe(
  id: string,
  filters: readonly FilterSpec[],
  columns: Columns,
  modelIndex: number,
  visiting: ReadonlySet<string>
): Rendered | undefined {
  const f = filters.find((x) => x.id === id);
  if (!f || visiting.has(id)) return undefined;
  if (f.type === 'combined') {
    return describeCombined(f, filters, columns, modelIndex, new Set(visiting).add(id));
  }
  const text = describeLeaf(f, columns);
  return text === undefined ? undefined : { text, kind: 'atom' };
}

function describeLeaf(f: FilterSpec, columns: Columns): string | undefined {
  const eq = equality(f, columns);
  if (eq) return `${eq.column} = ${eq.value}`;
  if (f.type !== 'continuous') return undefined;
  const column = columnName(f.continuous_column, columns);
  if (!column) return undefined;
  const min = typeof f.min_limit_value === 'number' ? f.min_limit_value : undefined;
  const max = typeof f.max_limit_value === 'number' ? f.max_limit_value : undefined;
  // Bounds are inclusive: an equality runs as [n, n] (numericCategoricalToContinuous).
  if (min !== undefined && max !== undefined) return `${min} ≤ ${column} ≤ ${max}`;
  if (min !== undefined) return `${column} ≥ ${min}`;
  if (max !== undefined) return `${column} ≤ ${max}`;
  return undefined;
}

// A leaf that keeps one value of one column: a categorical, or the single-point
// continuous a numeric-looking categorical is lowered to (SPEC/2233) — which is
// what the user actually picked, so it reads as `=`, not as a range.
function equality(f: FilterSpec, columns: Columns): Equality | undefined {
  if (f.type === 'categorical') {
    const column = columnName(f.category_column, columns);
    const value = String(f.category_value ?? '');
    return column && value.trim() !== '' ? { column, value } : undefined;
  }
  if (
    f.type === 'continuous' &&
    typeof f.min_limit_value === 'number' &&
    f.min_limit_value === f.max_limit_value
  ) {
    const column = columnName(f.continuous_column, columns);
    return column ? { column, value: String(f.min_limit_value) } : undefined;
  }
  return undefined;
}

function columnName(position: number, columns: Columns): string | undefined {
  return columns?.[position]?.name || undefined;
}

function describeCombined(
  f: CombinedFilter,
  filters: readonly FilterSpec[],
  columns: Columns,
  modelIndex: number,
  visiting: ReadonlySet<string>
): Rendered | undefined {
  // The `error` cut-off reads as its own "ley de corte ≥ c" segment.
  const tokens = expressionWithoutCutoff(f).match(TOKEN) ?? [];
  if (tokens.length === 0) return undefined;
  // Dropping the period operand out of an arbitrary boolean expression changes
  // its meaning, so a combined over it is not described here at all.
  if (tokens.some((t) => t === periodOrFilterId(modelIndex) || isPeriodLeafId(t, modelIndex))) {
    return undefined;
  }

  return renderTokens(tokens, filters, columns, modelIndex, visiting);
}

type Equality = { column: string; value: string };
type Operand = { not: boolean; rendered: Rendered; eq?: Equality };

// One expression or parenthesised sub-expression, split at its own level into
// operands and operators (parentheses wrapping the whole of it are dropped, each
// inner group is rendered the same way):
//   • only OR, each operand one value of the SAME column → `col = 5, 6`
//   • only AND → its operands joined with `; `, no parentheses; values of one
//     column merge into `col = 5 y 6`
//   • anything else keeps the AND/OR words as written
// Operands and binary operators must alternate (`NOT` only prefixes an operand);
// a malformed expression or unbalanced parentheses → undefined, never printed
// half-described.
function renderTokens(
  tokens: string[],
  filters: readonly FilterSpec[],
  columns: Columns,
  modelIndex: number,
  visiting: ReadonlySet<string>
): Rendered | undefined {
  const inner = withoutOuterParens(tokens);
  const operands: Operand[] = [];
  const operators: string[] = [];
  let expectOperand = true;
  let not = false;
  for (let i = 0; i < inner.length; i++) {
    const t = inner[i];
    if (t === 'NOT') {
      if (!expectOperand || not) return undefined;
      not = true;
      continue;
    }
    if (t === 'AND' || t === 'OR') {
      if (expectOperand) return undefined;
      operators.push(t);
      expectOperand = true;
      continue;
    }
    if (!expectOperand) return undefined;
    expectOperand = false;
    let rendered: Rendered | undefined;
    let eq: Equality | undefined;
    if (t === '(') {
      const close = matchingParen(inner, i);
      if (close < 0) return undefined;
      rendered = renderTokens(inner.slice(i + 1, close), filters, columns, modelIndex, visiting);
      i = close;
    } else if (ID.test(t)) {
      rendered = describe(t, filters, columns, modelIndex, visiting);
      const leaf = filters.find((x) => x.id === t);
      eq = leaf && leaf.type !== 'combined' ? equality(leaf, columns) : undefined;
    }
    if (!rendered) return undefined;
    operands.push({ not, rendered, eq });
    not = false;
  }
  if (expectOperand) return undefined;

  if (operands.length === 1) {
    const [only] = operands;
    return only.not ? { text: notText(only.rendered), kind: 'atom' } : only.rendered;
  }

  const values = operands.every((o) => !o.not && o.eq) ? operands.map((o) => o.eq!) : undefined;
  if (operators.every((o) => o === 'OR')) {
    if (values && values.every((v) => v.column === values[0].column)) {
      return { text: valueList(values[0].column, values, ', '), kind: 'atom' };
    }
    return { text: joinWords(operands, operators), kind: 'expr' };
  }
  if (operators.every((o) => o === 'AND')) return andChain(operands);
  return { text: joinWords(operands, operators), kind: 'expr' };
}

// An AND chain: `; ` between operands, no parentheses — an AND sub-chain simply
// continues it, only an expression keeping its own AND/OR words is grouped.
// Single values of one column merge, in order of first appearance.
function andChain(operands: Operand[]): Rendered {
  const items: (string | Equality[])[] = [];
  const byColumn = new Map<string, Equality[]>();
  for (const o of operands) {
    if (!o.not && o.eq) {
      const group = byColumn.get(o.eq.column);
      if (group) group.push(o.eq);
      else {
        const fresh = [o.eq];
        byColumn.set(o.eq.column, fresh);
        items.push(fresh);
      }
      continue;
    }
    if (o.not) items.push(notText(o.rendered));
    else items.push(o.rendered.kind === 'expr' ? `(${o.rendered.text})` : o.rendered.text);
  }
  const texts = items.map((it) =>
    typeof it === 'string' ? it : valueList(it[0].column, it, ' y ')
  );
  // Still an AND when it merged into one `col = 5 y 6` — grouped under OR/NOT.
  // Only a repeated single value (`a AND a`) is a plain atom.
  const [first] = items;
  const single =
    items.length === 1 &&
    typeof first !== 'string' &&
    new Set(first.map((v) => v.value)).size === 1;
  return { text: texts.join('; '), kind: single ? 'atom' : 'and' };
}

// Operands under OR or a mix of operators: the words stay, and anything that is
// not an atom is grouped so the reader sees what each operator applies to.
function joinWords(operands: Operand[], operators: string[]): string {
  return operands
    .map((o, i) => {
      const text = o.not
        ? notText(o.rendered)
        : o.rendered.kind === 'atom'
          ? o.rendered.text
          : `(${o.rendered.text})`;
      return i === 0 ? text : `${operators[i - 1]} ${text}`;
    })
    .join(' ');
}

function notText(r: Rendered): string {
  return `NOT ${r.kind === 'atom' ? r.text : `(${r.text})`}`;
}

function valueList(column: string, values: Equality[], separator: string): string {
  const distinct = Array.from(new Set(values.map((v) => v.value)));
  return `${column} = ${distinct.join(separator)}`;
}

// Strip parentheses that wrap the whole expression: `((a OR b))` → `a OR b`.
function withoutOuterParens(tokens: string[]): string[] {
  let t = tokens;
  while (t.length >= 2 && t[0] === '(' && matchingParen(t, 0) === t.length - 1) {
    t = t.slice(1, -1);
  }
  return t;
}

// Index of the parenthesis closing the one opened at `open`, or -1.
function matchingParen(tokens: string[], open: number): number {
  let depth = 0;
  for (let i = open; i < tokens.length; i++) {
    if (tokens[i] === '(') depth++;
    else if (tokens[i] === ')') depth--;
    if (depth === 0) return i;
  }
  return -1;
}
